import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { toast } from "react-toastify";
import {
  addApplicantRequest,
  getApplicants,
  removeApplicant,
  updateApplicantRequest,
} from "../../../../services/api/landing/careers/applicants/applicantsApi";

const getLang = () =>
  localStorage.getItem("language") === "ar" ? "ar" : "en";

const toastMessages = {
  add: { en: "Applicant added successfully!", ar: "تم إضافة المتقدم بنجاح!" },
  addError: { en: "Failed to add applicant.", ar: "فشل في إضافة المتقدم." },
  update: {
    en: "Applicant updated successfully!",
    ar: "تم تحديث المتقدم بنجاح!",
  },
  updateError: {
    en: "Failed to update applicant.",
    ar: "فشل في تحديث المتقدم.",
  },
  delete: {
    en: "Applicant deleted successfully!",
    ar: "تم حذف المتقدم بنجاح!",
  },
  deleteError: {
    en: "Failed to delete applicant.",
    ar: "فشل في حذف المتقدم.",
  },
};

export const fetchApplicants = createAsyncThunk(
  "applicants/fetchApplicants",
  async (dateRange = {}, { rejectWithValue }) => {
    try {
      const response = await getApplicants(dateRange);
      return response.data.data;
    } catch (error) {
      return rejectWithValue("Failed to fetch applicants");
    }
  },
);

export const addApplicant = createAsyncThunk(
  "applicants/addApplicant",
  async (formData, { rejectWithValue }) => {
    try {
      const response = await addApplicantRequest(formData);
      return response.data.data;
    } catch (error) {
      if (error.response && error.response.data) {
        return rejectWithValue(
          error.response.data.message || "Failed to add applicant",
        );
      }
      return rejectWithValue("Failed to add applicant");
    }
  },
);

export const updateApplicant = createAsyncThunk(
  "applicants/updateApplicant",
  async ({ id, applicantData }, { rejectWithValue }) => {
    try {
      const response = await updateApplicantRequest(id, applicantData);
      return response.data.data;
    } catch (error) {
      if (error.response && error.response.data) {
        return rejectWithValue(
          error.response.data.message || "Failed to update applicant",
        );
      }
      return rejectWithValue("Failed to update applicant");
    }
  },
);

export const deleteApplicant = createAsyncThunk(
  "applicants/deleteApplicant",
  async (applicantId, { rejectWithValue }) => {
    try {
      await removeApplicant(applicantId);
      return applicantId;
    } catch (error) {
      return rejectWithValue("Failed to delete applicant");
    }
  },
);

const applicantSlice = createSlice({
  name: "applicants",
  initialState: {
    applicantData: [],
    loading: false,
    error: null,
  },
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchApplicants.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchApplicants.fulfilled, (state, action) => {
        state.loading = false;
        state.applicantData = action.payload;
      })
      .addCase(fetchApplicants.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(addApplicant.fulfilled, (state, action) => {
        state.applicantData.push(action.payload);
        toast.success(toastMessages.add[getLang()]);
      })
      .addCase(addApplicant.rejected, (state, action) => {
        state.error = action.payload;
        toast.error(toastMessages.addError[getLang()]);
      })
      .addCase(updateApplicant.fulfilled, (state, action) => {
        const index = state.applicantData.findIndex(
          (applicant) => applicant.id === action.payload.id,
        );
        if (index !== -1) {
          state.applicantData[index] = action.payload;
        }
        toast.success(toastMessages.update[getLang()]);
      })
      .addCase(updateApplicant.rejected, (state, action) => {
        state.error = action.payload;
        toast.error(toastMessages.updateError[getLang()]);
      })
      .addCase(deleteApplicant.fulfilled, (state, action) => {
        state.applicantData = state.applicantData.filter(
          (applicant) => applicant.id !== action.payload,
        );
        toast.success(toastMessages.delete[getLang()]);
      })
      .addCase(deleteApplicant.rejected, (state, action) => {
        state.error = action.payload;
        toast.error(toastMessages.deleteError[getLang()]);
      });
  },
});

export default applicantSlice.reducer;
